import styled from "@emotion/styled";
import { useEffect, useRef, useState } from "react";
import VisibilityWrapper from "./VisibilityWrapper";

export const MAX_BARS = 5;
const TICK_MS = 1000;

function KWHBank({ isDaytime, tags }) {
  const [count, setCount] = useState(0);
  const timeoutRef = useRef();
  const barsRef = useRef(null);
  const prevIsDaytimeRef = useRef(isDaytime);

  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (tags?.includes("visible")) {
      if (!visible) {
        setCount(isDaytime ? 0 : MAX_BARS);
      }
      setVisible(true);
    }
    if (tags?.includes("hidden")) {
      if (visible) {
        setCount(isDaytime ? 0 : MAX_BARS);
      }
      setVisible(false);
    }
  }, [tags, isDaytime, visible]);

  useEffect(() => {
    if (!visible) return;

    if (prevIsDaytimeRef.current !== isDaytime) {
      prevIsDaytimeRef.current = isDaytime;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }
  }, [isDaytime, visible]);

  useEffect(() => {
    if (!visible) return;

    if (isDaytime && count < 0) {
      setCount(0);
      return;
    }
    if (!isDaytime && count > MAX_BARS) {
      setCount(MAX_BARS);
      return;
    }

    if (isDaytime && count >= MAX_BARS) return;
    if (!isDaytime && count <= 0) return;

    timeoutRef.current = setTimeout(() => {
      setCount((prev) => {
        const next = prev + (isDaytime ? 1 : -1);
        requestAnimationFrame(() => {
          if (barsRef.current) {
            void barsRef.current.offsetHeight;
          }
        });
        return next;
      });
    }, TICK_MS);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [count, isDaytime, visible]);

  const filledCount = Math.max(0, Math.min(MAX_BARS, count));
  const barColor = isDaytime ? "#aade03" : "#b4e700";

  return (
    <VisibilityWrapper tags={tags}>
      <Wrapper>
        <Text style={{ color: isDaytime ? "#4c4747" : "#bfbebe" }}>
          Banked Kilowatt Hours
        </Text>

        <BarsWrapper
          ref={barsRef}
          color={isDaytime ? "#4c4747" : "#bfbebe"}
          data-filled={filledCount}
        >
          {new Array(MAX_BARS)
            .fill(null)
            .map((_, i) => (
              <BarSlot key={i}>
                <BarFill
                  $filled={i < filledCount}
                  $background={barColor}
                  data-testid={`bank-bar-${i}`}
                >
                  kWh
                </BarFill>
              </BarSlot>
            ))
            .reverse()}
        </BarsWrapper>
      </Wrapper>
    </VisibilityWrapper>
  );
}

export default KWHBank;

const Wrapper = styled.div`
  position: absolute;
  top: 18%;
  right: 4em;
  height: 16%;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 20%;
`;

const Text = styled.p`
  position: absolute;
  top: -3em;
  font-size: 16px;
  font-weight: bold;
  white-space: nowrap;
  text-align: center;
`;

const BarsWrapper = styled.div`
  width: 40%;
  height: 100%;
  align-self: center;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  border: 2px dashed ${({ color }) => color};
  box-sizing: border-box;
`;

const BarSlot = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  overflow: hidden;

  &:first-of-type > div {
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
  }

  &:last-of-type > div {
    border-bottom-right-radius: 6px;
    border-bottom-left-radius: 6px;
  }
`;

const BarFill = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 12px;
  background: ${({ $background }) => $background};
  height: ${({ $filled }) => ($filled ? "100%" : "0")};
  padding: ${({ $filled }) => ($filled ? "1px" : "0")};
  margin: ${({ $filled }) => ($filled ? "1px" : "0")};
  box-sizing: border-box;
  overflow: hidden;
  transition: height 0.4s ease, padding 0.4s ease, margin 0.4s ease;
`;
